import {
	formatNotificationTime,
	getNotificationConfig,
	getNotificationDestination,
	type NotificationMetadata,
	notificationMetadataSchema,
} from "@upsquad/shared";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button } from "@/src/common/components/ui";
import { cn } from "@/src/common/lib/utils";
import { useAppDispatch, useAppSelector } from "@/src/common/redux/store";
import { getActiveCommunityId } from "@/src/community/store/community-selectors";
import { setActiveCommunityId } from "@/src/community/store/community-slice";
import type { ApiNotification } from "../services/notification-http.service";
import { getNotificationUrl } from "../utils/get-notification-url";
import { NOTIFICATION_COLOR_CLASSES, NOTIFICATION_ICONS } from "../utils/notification-display-map";

interface NotificationItemProps {
	notification: ApiNotification;
	onMarkRead?: (id: string) => void;
	onMarkUnread?: (id: string) => void;
	showReadToggle?: boolean;
	onClose?: () => void;
}

export function NotificationItem({ notification, onMarkRead, onMarkUnread, showReadToggle, onClose }: NotificationItemProps) {
	const { t, i18n } = useTranslation("notification");
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const activeCommunityId = useAppSelector(getActiveCommunityId);
	const isUnread = !notification.read_at;
	const { iconKey, colorKey } = getNotificationConfig(notification.type);
	const Icon = NOTIFICATION_ICONS[iconKey];

	const parsedMetadata = notificationMetadataSchema.safeParse(notification.metadata ?? {});
	const metadata: NotificationMetadata = parsedMetadata.success ? parsedMetadata.data : {};
	const notificationCommunityName = metadata.community_name;

	const handleClick = () => {
		if (isUnread && onMarkRead) {
			onMarkRead(notification.id);
		}
		if (metadata.community_id && metadata.community_id !== activeCommunityId) {
			dispatch(setActiveCommunityId(metadata.community_id));
		}
		const destination = getNotificationDestination(notification.type, metadata);
		if (destination) {
			navigate(getNotificationUrl(destination));
		}
		onClose?.();
	};

	const handleToggleRead = () => {
		if (isUnread) {
			onMarkRead?.(notification.id);
		} else {
			onMarkUnread?.(notification.id);
		}
	};

	return (
		<button
			type="button"
			className={cn(
				"group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-default-100",
				isUnread && "bg-primary-50/50",
			)}
			onClick={handleClick}
		>
			<div className={cn("mt-0.5 shrink-0", NOTIFICATION_COLOR_CLASSES[colorKey])}>
				<Icon size={18} />
			</div>

			<div className="min-w-0 flex-1">
				<p className={cn("text-sm text-foreground", isUnread && "font-semibold")}>{notification.title}</p>
				<p className="text-xs text-foreground/60">{notification.content}</p>
				<p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-foreground/40">
					<span>{formatNotificationTime(notification.created_at, t, i18n.language)}</span>
					{notificationCommunityName && (
						<>
							<span aria-hidden="true">·</span>
							<span>{notificationCommunityName}</span>
						</>
					)}
				</p>
			</div>

			<div className="flex shrink-0 items-center gap-2">
				{isUnread && <span className="size-2 rounded-full bg-primary" />}
				{showReadToggle && (
					<Button
						isIconOnly
						variant="ghost"
						size="sm"
						className="opacity-0 group-hover:opacity-100"
						aria-label={isUnread ? t("mark_as_read") : t("mark_as_unread")}
						onPress={handleToggleRead}
					>
						{isUnread ? <Eye size={14} /> : <EyeOff size={14} />}
					</Button>
				)}
			</div>
		</button>
	);
}
